import { useEffect, useState } from "react";
import { api, formatBRL } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

const EMPTY = {
  name: "", slug: "", category: "", material: "",
  description: "", price: 0, sale_price: null,
  images: [], sizes: [], variations: [],
  stock: 0, ncm: "", is_featured: false, is_new: true, is_archived: false,
};

export default function AdminProducts() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [filterMat, setFilterMat] = useState("");
  const [sortBy, setSortBy] = useState("new");

  const load = () => {
    const params = {};
    if (filterCat) params.category = filterCat;
    if (filterMat) params.material = filterMat;
    if (sortBy) params.sort = sortBy;
    api.get("/admin/products", { params }).then(({ data }) => setList(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, filterMat, sortBy]);
  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setCats(data));
    api.get("/admin/materials").then(({ data }) => setMaterials(data));
    api.get("/admin/sizes").then(({ data }) => setSizes(data));
  }, []);

  const save = async (form) => {
    const payload = { ...form, price: Number(form.price), sale_price: form.sale_price ? Number(form.sale_price) : null, stock: Number(form.stock) || 0 };
    try {
      if (editing?.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Produto salvo");
      setEditing(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Remover este produto definitivamente?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Produto removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover");
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl text-[#49392F]">Produtos</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="duo-btn duo-btn-primary" data-testid="admin-new-product">
          <Plus size={14} /> Novo produto
        </button>
      </div>

      <div className="bg-white border border-[#C2A064]/20 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <select className="duo-input" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="admin-filter-category">
          <option value="">Todas as categorias</option>
          {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select className="duo-input" value={filterMat} onChange={(e) => setFilterMat(e.target.value)} data-testid="admin-filter-material">
          <option value="">Todos os materiais</option>
          {materials.map((m) => <option key={m.id || m.name} value={m.name}>{m.name}</option>)}
        </select>
        <select className="duo-input" value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="admin-sort-select">
          <option value="new">Mais recentes</option>
          <option value="stock_asc">Estoque: menor → maior</option>
          <option value="stock_desc">Estoque: maior → menor</option>
          <option value="price_asc">Preço: menor → maior</option>
          <option value="price_desc">Preço: maior → menor</option>
        </select>
      </div>

      <div className="bg-white border border-[#C2A064]/20 overflow-x-auto">
        <table className="w-full text-[13px] min-w-[700px]">
          <thead className="text-left text-[#49392F]/70">
            <tr>
              <th className="p-3">Produto</th><th className="p-3">Categoria</th><th className="p-3">Material</th>
              <th className="p-3 text-right">Preço</th><th className="p-3 text-right">Estoque</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#49392F]/60">Nenhum produto cadastrado.</td></tr>}
            {list.map((p) => (
              <tr key={p.id} className="border-t border-[#C2A064]/10">
                <td className="p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F8F5EF] overflow-hidden">
                    {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                  </div>
                  <span>{p.name} {p.is_archived && <span className="text-[10px] text-[#49392F]/50 ml-2">(arquivado)</span>}</span>
                </td>
                <td className="p-3">{p.category}</td>
                <td className="p-3">{p.material}</td>
                <td className="p-3 text-right">{formatBRL(p.sale_price || p.price)}</td>
                <td className="p-3 text-right">{p.stock}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing(p)} className="p-2 text-[#49392F]/70 hover:text-[#49392F]" aria-label="editar"><Pencil size={14} /></button>
                  <button onClick={() => del(p.id)} className="p-2 text-[#49392F]/70 hover:text-[#49392F]" aria-label="remover"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductForm product={editing} categories={cats} materials={materials} allSizes={sizes} onClose={() => setEditing(null)} onSave={save} />
      )}
    </div>
  );
}

function ProductForm({ product, categories, materials, allSizes, onClose, onSave }) {
  const [form, setForm] = useState(product);
  const [variationInput, setVariationInput] = useState("");

  const toggleSize = (label) => {
    setForm((f) => {
      const cur = f.sizes || [];
      return { ...f, sizes: cur.includes(label) ? cur.filter((s) => s !== label) : [...cur, label] };
    });
  };

  const addVar = () => { if (variationInput.trim()) { setForm({...form, variations: [...(form.variations||[]), variationInput.trim()]}); setVariationInput(""); } };
  const removeVar = (i) => setForm({...form, variations: form.variations.filter((_, idx) => idx !== i)});

  const submit = (e) => { e.preventDefault(); onSave(form); };

  // Smart size suggestions by category
  const groupMap = { "anéis": "aneis", "aneis": "aneis", "alianças": "aneis", "aliancas": "aneis",
    "colares": "colar", "colar": "colar", "correntes": "colar",
    "pulseiras": "pulseira", "pulseira": "pulseira",
    "brincos": "brinco", "brinco": "brinco" };
  const grp = groupMap[(form.category || "").toLowerCase()];
  const suggestedSizes = grp ? (allSizes || []).filter((s) => s.group === grp) : [];

  return (
    <div className="fixed inset-0 bg-[#49392F]/40 z-50 flex items-start justify-center overflow-auto py-10" onClick={onClose}>
      <div className="bg-white border border-[#C2A064]/30 w-full max-w-3xl mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#C2A064]/20">
          <h2 className="font-display text-xl text-[#49392F]">{product.id ? "Editar produto" : "Novo produto"}</h2>
          <button onClick={onClose} className="text-[#49392F]/60"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Nome *" className="duo-input col-span-2" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="pf-name" />
            <select required className="duo-input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="pf-category">
              <option value="">Categoria *</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <select required className="duo-input" value={form.material} onChange={(e) => setForm({...form, material: e.target.value})} data-testid="pf-material">
              <option value="">Material *</option>
              {(materials || []).map((m) => <option key={m.id || m.name} value={m.name}>{m.name}</option>)}
            </select>
            <textarea rows={3} placeholder="Descrição" className="duo-input col-span-2" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
            <input required type="number" step="0.01" placeholder="Preço *" className="duo-input" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} data-testid="pf-price" />
            <input type="number" step="0.01" placeholder="Preço promocional" className="duo-input" value={form.sale_price || ""} onChange={(e) => setForm({...form, sale_price: e.target.value || null})} />
            <input type="number" placeholder="Estoque" className="duo-input" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} data-testid="pf-stock" />
            <input placeholder="NCM (opcional)" className="duo-input" value={form.ncm || ""} onChange={(e) => setForm({...form, ncm: e.target.value})} />
          </div>

          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/80 mb-2">Imagens do produto</div>
            <ImageUploader value={form.images || []} onChange={(v) => setForm({...form, images: v})} max={8} />
          </div>

          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/80 mb-2">
              Tamanhos disponíveis {form.category && <span className="text-[#49392F]/50 normal-case tracking-normal">— sugeridos para {form.category}</span>}
            </div>
            {suggestedSizes.length === 0 ? (
              <div className="text-[12px] text-[#49392F]/60">Cadastre tamanhos em "Tamanhos" no menu.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {suggestedSizes.map((s) => {
                  const active = (form.sizes || []).includes(s.label);
                  return (
                    <button
                      key={s.id || s.label}
                      type="button"
                      onClick={() => toggleSize(s.label)}
                      className={`min-w-[44px] h-10 px-3 text-[13px] border ${active ? "bg-[#49392F] text-white border-[#49392F]" : "bg-white border-[#E8DCCB] text-[#49392F] hover:border-[#49392F]"}`}
                      data-testid={`pf-size-${s.label}`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-[#49392F]/60 mt-2">Deixe sem seleção se o produto não tem tamanhos.</p>
          </div>
          <TagInput label="Variações" value={variationInput} setValue={setVariationInput} items={form.variations} onAdd={addVar} onRemove={removeVar} placeholder="Ex.: Dourado, Prateado..." />

          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm({...form, is_featured: e.target.checked})} /> Destaque</label>
            <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={!!form.is_new} onChange={(e) => setForm({...form, is_new: e.target.checked})} /> Novidade</label>
            <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={!!form.is_archived} onChange={(e) => setForm({...form, is_archived: e.target.checked})} /> Arquivado</label>
            <label className="inline-flex items-center gap-2 text-[13px]" title="Para alianças compradas em par com nomes personalizados">
              <input type="checkbox" checked={!!form.is_custom_pair} onChange={(e) => setForm({...form, is_custom_pair: e.target.checked})} data-testid="pf-custom-pair" />
              Aliança personalizada (par com nomes)
            </label>
          </div>

          <div className="flex gap-3 pt-4 border-t border-[#C2A064]/20">
            <button type="submit" className="duo-btn duo-btn-primary" data-testid="pf-save">Salvar</button>
            <button type="button" onClick={onClose} className="duo-btn duo-btn-secondary">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TagInput({ label, value, setValue, items, onAdd, onRemove, placeholder }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/80 mb-2">{label}</div>
      <div className="flex gap-2">
        <input className="duo-input flex-1" value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); }}} />
        <button type="button" onClick={onAdd} className="duo-btn duo-btn-secondary">Adicionar</button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {items?.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-2 bg-[#E8DCCB]/60 text-[#49392F] text-[11px] px-2 py-1 border border-[#C2A064]/30">
            {it.length > 50 ? `${it.slice(0, 50)}...` : it}
            <button type="button" onClick={() => onRemove(i)} className="text-[#49392F]/60"><X size={10} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}
